// Port of Flutter's roomsScreen.dart.
//
//   AppBar   avatar · location name · "<Name> <Surname>"
//   body     common-devices strip, the sensor strip, then the room list
//   room row ButtonColor card (elevation 4, radius 12) with a location pin,
//            the room name, an "Add: <ADD1>" subtitle and an ↑ chevron
//   FAB      Add Common Device · Add Room
//
// Rooms and common devices are drawn in the customer's own `SortOrder` — the
// desktop shell always did and this one did not, which is how one house came to
// have two orders. See core/utils/sortOrder.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, ListItemButton, ListItemIcon, ListItemText, Typography, useTheme,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowUpwardSharpIcon from '@mui/icons-material/ArrowUpwardSharp';
import AddIcon from '@mui/icons-material/Add';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import WifiIcon from '@mui/icons-material/Wifi';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ProfileTitleBar } from '../components/MobileAppBar';
import CommonDevicesStrip from '../components/CommonDevicesStrip';
import SensorStrip from '../components/SensorStrip';
import CircleMenu from '../components/CircleMenu';
import AddRoomDialog from '../components/AddRoomDialog';
import UnmapRoomDialog from '../components/UnmapRoomDialog';
import useLongPress from '../components/useLongPress';
import UpdateCodeDialog from '../../shared/UpdateCodeDialog';
import WifiFleetDialog from '../../shared/WifiFleetDialog';
import ShareQrDialog from '../../shared/ShareQrDialog';
import RoomPresenceBadge from '../../shared/RoomPresenceBadge';
import useRoomPresence from '../../../core/hooks/useRoomPresence';
import { useApp } from '../../../core/state/AppContext';
import {
  getRooms, getCommonDevices, updateRoomSortOrder, getRfDevices,
} from '../../../core/api/api';
import RfSensorDialog from '../../shared/RfSensorDialog';
import SecurityBar from '../../shared/SecurityBar';
import { arrayMove } from '../../../core/utils/reorder';
import ReorderArrows, { EditOrderButton } from '../components/ReorderBar';
import { sortByOrder } from '../../../core/utils/sortOrder';
import { splitSensors } from '../../../core/devices/sensorState';
import { useHideOffline, useOnlineOnly } from '../../../core/hooks/useHideOffline';
import { drillFromRooms, arrivedViaBack, parentOf } from '../../../core/nav/appNav';
import { withoutHubs } from '../../../core/devices/hubDevice';

// ⚠️ A COMPONENT, not JSX inlined in the map. `useLongPress` is a hook, and one
// per room inside the .map callback would be a hook called in a loop — the
// count changes as rooms load, which is precisely what the rules of hooks
// forbid. The same pattern the Locations tab already uses for its cards.
function RoomRow({ room, onOpen, onUnmap, reorder, presence }) {
  const theme = useTheme();
  const c = theme.sli;
  // Long-press for the destructive action, tap to open — the gesture the
  // Locations tab already uses to reach a location's details, so it is not a
  // new thing to learn.
  //
  // ⚠️ THE HOOK RUNS UNCONDITIONALLY, THE PROPS DO NOT. Handing useLongPress a
  // no-op for users who may not unmap does NOT degrade to a plain button: it
  // still latches `fired` on a held finger and then suppresses the click that
  // follows, so an ordinary user who rests their thumb on a room finds it
  // simply does not open. The hook has to be called every render (rules of
  // hooks), so the choice is made when spreading.
  const press = useLongPress(onUnmap || (() => {}), onOpen);
  const handlers = onUnmap ? press : { onClick: onOpen };

  return (
    <Card elevation={4} sx={{ bgcolor: c.button, borderRadius: '12px', mx: 2, my: 1 }}>
      <ListItemButton sx={{ p: 1.5, borderRadius: '12px' }} {...handlers}>
        <ListItemIcon sx={{ color: c.text, minWidth: 40 }}>
          <LocationOnIcon />
        </ListItemIcon>
        <ListItemText
          primary={room.RoomName || 'No Room Name'}
          secondary={`Add: ${room.ADD1 ?? 'Unknown'}`}
          primaryTypographyProps={{ sx: { color: c.text, fontSize: 15, fontWeight: 700 } }}
          secondaryTypographyProps={{ sx: { color: c.text, fontSize: 14 } }}
        />
        {/* Smart room, someone inside. No caption here: the row's long-press
            is Unmap, and a tooltip would fight it. */}
        <RoomPresenceBadge presence={presence} size="medium" sx={{ mx: 1 }} />
        {reorder ? (
          <ReorderArrows
            index={reorder.index}
            count={reorder.count}
            onMove={reorder.onMove}
            color={c.text}
          />
        ) : (
          <ArrowUpwardSharpIcon sx={{ color: c.text }} />
        )}
      </ListItemButton>
    </Card>
  );
}

export default function MobileRoomsScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { locationId } = useParams();
  const { state } = useLocation();
  const {
    user, userId, locations, toggleFavourite, canUpdateFirmware,
    canReorder, editOrder, setEditOrder, showCommon, setShowCommon,
  } = useApp();

  // Rooms.SortOrder is GLOBAL, not per-user — one column shared by everyone who
  // can see the room, exactly as the desktop's own note records. Reordering
  // here changes the order for every other user of the location too.
  const moveRoom = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || !rooms || j >= rooms.length) return;
    const next = arrayMove(rooms, i, j);
    setRooms(next.map((r, k) => ({ ...r, SortOrder: k })));
    await Promise.all(next.map((r, k) => updateRoomSortOrder(r.RoomID, k).catch(() => null)));
  };

  const [rooms, setRooms] = useState(null);   // null = still loading
  const [common, setCommon] = useState([]);
  const [addRoomOpen, setAddRoomOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [wifiOpen, setWifiOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  // The room being unmapped, or null. Held rather than a boolean because the
  // dialog counts that room's devices before it will let the write happen.
  const [unmapRoom, setUnmapRoom] = useState(null);
  const redirectedRef = useRef(false);
  // The person on an occupied smart room (door + motion sensor) — live.
  const { presenceOf } = useRoomPresence(locationId, { viewerUserId: userId });

  const locationName = state?.locationName
    || locations.find((l) => String(l.LocationID) === String(locationId))?.LocationName
    || 'Location';

  const load = useCallback(async () => {
    if (!userId || !locationId) return;
    const [rs, cs] = await Promise.all([
      getRooms(userId, locationId).catch(() => null),
      getCommonDevices(userId, locationId).then(withoutHubs).catch(() => null),
    ]);
    setCommon(sortByOrder(Array.isArray(cs?.data) ? cs.data : []));
    setRooms(sortByOrder(Array.isArray(rs?.data) ? rs.data : []));
  }, [userId, locationId]);

  useEffect(() => { load(); }, [load]);

  /*
   * ⚠️ "Hide offline devices" IS ONE SETTING FOR BOTH SHELLS — one localStorage
   * key, read by desktop and mobile alike (core/hooks/useHideOffline). Only the
   * desktop honoured it here, so a user who ticked it on a laptop saw offline
   * common devices disappear there and still listed on the phone, from the same
   * account, at the same moment. A setting that applies on one screen and not
   * the other is worse than not having it.
   *
   * Filtered BEFORE the sensor split, so the count covers both rows.
   */
  const [hideOffline] = useHideOffline();
  const { devices: liveCommon, hiddenCount } = useOnlineOnly(common, hideOffline);
  // A location's roomless sensors get the same row they get inside a room —
  // state on the card, not behind a tap. See core/devices/sensorState.
  const { sensors, controls } = useMemo(() => splitSensors(liveCommon), [liveCommon]);

  // ⚠️ WHOLE-LOCATION RF SENSORS (RoomID 0) — the main-door sensor of the
  // premises. They are in no room, so DevicesScreen (which asks by RoomID) never
  // showed them; they belong in this strip, beside the roomless device sensors.
  // Named and active only, as the room screen does. null = not loaded yet.
  const [rfAtLocation, setRfAtLocation] = useState(null);
  const [rfEditing, setRfEditing] = useState(null);
  const loadRf = useCallback(() => {
    if (!locationId) { setRfAtLocation([]); return; }
    getRfDevices({ LocationID: locationId, ViewerUserId: userId })
      .then((res) => setRfAtLocation((Array.isArray(res.data) ? res.data : [])
        .filter((d) => String(d.Name || '').trim() && Number(d.Active) === 1 && !(Number(d.RoomID) > 0))))
      // An older backend has no RF endpoint; the location must still draw.
      .catch(() => setRfAtLocation([]));
  }, [locationId, userId]);
  useEffect(() => { loadRf(); }, [loadRf]);

  // Flutter's _initialRender, widened: a location with exactly ONE thing to tap
  // drops straight into it, so the user isn't asked to choose from a list of
  // one. Both lists count — one room and one common device is a choice, and a
  // lone common device is opened directly because it lives in no room.
  //
  // ⚠️ NOT WHEN THE USER ARRIVED BY GOING BACK. This screen is the only way to
  // Add Room, Add Common Device and the location-wide Update Code; drilling
  // forward again on a back would put all three permanently out of reach for a
  // single-room site, and the user could never leave the device. See
  // core/nav/appNav.js.
  //
  // ⚠️ `rooms == null` MEANS STILL LOADING, and `common` starts as []. Deciding
  // before both have landed reads a one-room location as having nothing at all.
  useEffect(() => {
    // Waits for the RF sensors too: a location's door sensor is something to
    // SEE here, and drilling past it into the only room would hide it.
    if (rooms == null || rfAtLocation == null || redirectedRef.current || arrivedViaBack(state)) return;
    const target = drillFromRooms({
      rooms, common: [...common, ...rfAtLocation], locationId, locationName,
    });
    if (!target) return;
    redirectedRef.current = true;
    navigate(target.path, { replace: true, state: target.state });
  }, [rooms, common, rfAtLocation, state, navigate, locationName, locationId]);

  // `room` is passed for a device listed under one, and omitted for the common
  // strip — those belong to the LOCATION, so back should land here, not on a
  // room device list they are not in. See core/devices/deviceNav.js.
  const openDevice = (d, room) => navigate(`/device/${d.UniqueNumber}`, {
    state: {
      device: d,
      from: {
        locationId,
        locationName,
        roomId: room?.RoomID ?? null,
        roomName: room?.RoomName ?? '',
      },
    },
  });

  return (
    <Box sx={{ height: '100%', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      {/* The arrow and the Android back button must give the same answer, so
          both go through parentOf. History would send a drilled-into location
          back out of the app, because the drill replaced the entry. */}
      <ProfileTitleBar
        title={locationName}
        subtitle={`${user?.Name || ''} ${user?.Surname || ''}`.trim()}
        actions={canReorder ? (
          <EditOrderButton
            editing={editOrder}
            onToggle={() => setEditOrder((v) => !v)}
            color={c.text}
          />
        ) : null}
        onBack={() => {
          const t = parentOf(`/rooms/${locationId}`, state);
          if (t) navigate(t.path, { state: t.state });
          else navigate(-1);
        }}
      />

      {/* PINNED. Two independent scroll areas, as in the Dart (a fixed row above
          an Expanded list): common devices stay on screen and scroll sideways in
          their own container, while only the room list scrolls vertically. */}
      {/* The strip is now switchable on the phone too — one setting shared with
          the desktop, defaulting to shown here. See AppContext's showCommon for
          why the DEFAULT is per-shell and the stored choice is not. */}
      {showCommon && (
        <CommonDevicesStrip
          devices={controls}
          hiddenCount={hiddenCount}
          onOpen={openDevice}
          onToggleFavourite={async (d, fav) => { await toggleFavourite(d, fav); load(); }}
        />
      )}

      {/* 🔓/🔒 for the premises, "is anyone inside", alerts, auto-lock. */}
      <SecurityBar locationId={Number(locationId) || 0} locationName={locationName} dense />

      <SensorStrip
        devices={sensors}
        rfSensors={rfAtLocation || []}
        onOpenRf={setRfEditing}
        onOpen={openDevice}
        onToggleFavourite={async (d, fav) => { await toggleFavourite(d, fav); load(); }}
      />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {rooms == null ? (
          <Box sx={{ textAlign: 'center', p: 2 }}>
            <Typography sx={{ color: c.text, fontSize: 20 }}>Loading...</Typography>
          </Box>
        ) : (
          rooms.map((r, i) => (
            <RoomRow
              key={r.RoomID}
              room={r}
              presence={presenceOf(r.RoomID)}
              onOpen={() => navigate(`/room/${r.RoomID}/devices`, {
                state: { roomName: r.RoomName, locationName, locationId },
              })}
              // Same gate as the location-wide pushes below: owner or Super
              // Admin. It rewrites somebody's access, so it is not a gesture an
              // ordinary member of the household should be able to find.
              onUnmap={canUpdateFirmware ? () => setUnmapRoom(r) : undefined}
              reorder={editOrder && canReorder
                ? { index: i, count: rooms.length, onMove: moveRoom }
                : null}
            />
          ))
        )}
      </Box>

      <CircleMenu
        items={[
          {
            icon: <AddIcon />,
            title: 'Add Common Device',
            onClick: () => navigate(`/add-device?locationId=${locationId}&roomId=0`),
          },
          {
            icon: <MeetingRoomIcon />,
            title: 'Add Room',
            onClick: () => setAddRoomOpen(true),
          },
          // In the FAB rather than the app bar: the bar already carries back,
          // avatar, title, subtitle and the order toggle, and a sixth control
          // there would crowd out the room name. Desktop's wording, so the two
          // read as the one setting they are.
          {
            icon: <GroupWorkIcon />,
            title: showCommon ? 'Hide common devices' : 'Show common devices',
            onClick: () => setShowCommon((v) => !v),
          },
          // Location-wide firmware push — owner / super admin only.
          ...(canUpdateFirmware ? [{
            icon: <SystemUpdateAltIcon />,
            title: 'Update Code (Location)',
            onClick: () => setUpdateOpen(true),
          }] : []),
          // ⚠️ NOT the same thing as "Add WiFi to Location" on the location
          // details screen. That one writes the site's Wi-Fi RECORD and tells no
          // device anything; this publishes ADDWIFI to every device here and
          // REBOOTS all of them. Same gate and same neighbour as Update Code,
          // because it carries the same weight.
          ...(canUpdateFirmware ? [{
            icon: <WifiIcon />,
            title: 'Add / remove Wi-Fi (Location)',
            onClick: () => setWifiOpen(true),
          }] : []),
          // The WHOLE location — every room and device, door locks included —
          // so owner / Super Admin only, as the server also insists.
          ...(canUpdateFirmware ? [{
            icon: <QrCode2Icon />,
            title: 'Share Location (QR)',
            onClick: () => setShareOpen(true),
          }] : []),
        ]}
      />

      <ShareQrDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        target={{ scope: 'location', locationId: Number(locationId) }}
        name={locationName}
      />

      <WifiFleetDialog
        open={wifiOpen}
        scope={{ type: 'location', id: locationId, name: locationName }}
        onClose={() => setWifiOpen(false)}
      />

      <UnmapRoomDialog
        open={!!unmapRoom}
        room={unmapRoom}
        locationId={locationId}
        locationName={locationName}
        onClose={() => setUnmapRoom(null)}
        onDone={() => { setUnmapRoom(null); load(); }}
      />

      <UpdateCodeDialog
        open={updateOpen}
        scope={{ type: 'location', id: locationId, name: locationName }}
        onClose={() => setUpdateOpen(false)}
      />

      {/* Tapping a whole-location sensor configures it where it is. */}
      <RfSensorDialog
        device={rfEditing}
        locationId={Number(locationId) || 0}
        onClose={() => setRfEditing(null)}
        onSaved={loadRf}
      />

      <AddRoomDialog
        open={addRoomOpen}
        locationId={locationId}
        onClose={() => setAddRoomOpen(false)}
        onAdded={() => { setAddRoomOpen(false); load(); }}
      />
    </Box>
  );
}
